/**
 * ProrogaFields — F-004 Phase 3 (IMPL-P3-003).
 *
 * Field group per la variante PROROGA. `Controller` + `useFormContext` sopra
 * `AdempimentoDraft` (il host monta il `FormProvider`).
 *
 * Pattern seguiti (plan IMPL-P3-003):
 *  - DurataFields.tsx: normalizzazione input date (Date | stringa ISO da
 *    autosave/restore), styling select nativo.
 *  - CanoneFields.tsx: hint advisory non bloccante `role="status" aria-live=
 *    "polite" text-amber-600` sulla coerenza del canone.
 *
 * Campi:
 *  - dataInizioProroga / dataFineProroga (Input type="date"). `min` di
 *    dataInizioProroga derivato dalla scadenza della pratica madre (prop
 *    `scadenzaMinISO`, calcolata dal host via `prefillFromParent`).
 *  - adeguamentoIstat (checkbox) rivela nuovoCanoneMensile/nuovoCanoneAnnuale.
 *  - Hint advisory amber su coerenza canone (non blocca).
 *  - Errore bloccante `text-destructive` da `adempimentoFullSchema` su
 *    dataFineProroga quando l'incremento durata è errato (il messaggio 4+4/3+2
 *    arriva già formattato da `applyAdempimentoRefinements`, Phase 1).
 *
 * NB: la tabella legale delle durate (`DURATA_PER_TIPOLOGIA`) è importata da
 * `@app/shared` se serve mostrare un hint "durate ammesse" — NON viene
 * duplicata localmente (Key Decision; `DurataFields.tsx` la duplica oggi ma qui
 * non si ripete quell'errore).
 */
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import type { AdempimentoDraft } from '@app/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { usePrefilledFields } from '../../hooks/usePrefilledFields';
import {
  MODIFIED_HINT_TEXT,
  PREFILLED_HINT_TEXT,
  PREFILLED_RING_CLASS,
  prefilledHintId,
} from '../prefilledFieldUtils';

export interface ProrogaFieldsProps {
  /**
   * Data minima (ISO `YYYY-MM-DD`) per `dataInizioProroga`, tipicamente la
   * scadenza naturale del contratto madre. Calcolata dal host via
   * `prefillFromParent` (dataScadenzaOriginale). Se assente, nessun `min`.
   */
  scadenzaMinISO?: string;
}

/** Normalizza un valore RHF (Date | stringa ISO | undefined) a `YYYY-MM-DD`. */
function toDateInputValue(raw: unknown): string {
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  if (typeof raw === 'string') return raw.slice(0, 10);
  return '';
}

export function ProrogaFields({ scadenzaMinISO }: ProrogaFieldsProps) {
  const {
    control,
    formState: { errors, dirtyFields },
  } = useFormContext<AdempimentoDraft>();

  const prefilledPaths = usePrefilledFields();
  const adeguamentoIstat = useWatch({ control, name: 'adeguamentoIstat' });
  const nuovoCanoneMensile = useWatch({ control, name: 'nuovoCanoneMensile' });
  const nuovoCanoneAnnuale = useWatch({ control, name: 'nuovoCanoneAnnuale' });

  // Errori tipizzati solo sulla variante proroga.
  const prorogaErrors = errors as Record<string, { message?: string } | undefined>;

  // Hint advisory (non blocca) sulla coerenza del NUOVO canone (mirror CanoneFields).
  const showCoerenzaHint =
    nuovoCanoneMensile != null &&
    nuovoCanoneAnnuale != null &&
    nuovoCanoneMensile > 0 &&
    nuovoCanoneAnnuale > 0 &&
    Math.abs(nuovoCanoneAnnuale - nuovoCanoneMensile * 12) >
      Math.max(nuovoCanoneMensile * 12 * 0.01, 0.01);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Data inizio proroga */}
        <div className="space-y-1">
          <Label htmlFor="data-inizio-proroga">Data inizio proroga *</Label>
          <Controller
            name="dataInizioProroga"
            control={control}
            render={({ field }) => (
              <Input
                id="data-inizio-proroga"
                type="date"
                min={scadenzaMinISO}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                value={toDateInputValue(field.value)}
                onChange={(e) =>
                  field.onChange(e.target.value ? new Date(e.target.value) : undefined)
                }
                aria-invalid={!!prorogaErrors.dataInizioProroga}
                aria-describedby={
                  prorogaErrors.dataInizioProroga ? 'data-inizio-proroga-error' : undefined
                }
              />
            )}
          />
          {prorogaErrors.dataInizioProroga && (
            <p id="data-inizio-proroga-error" className="text-xs text-destructive">
              {prorogaErrors.dataInizioProroga.message}
            </p>
          )}
        </div>

        {/* Data fine proroga */}
        <div className="space-y-1">
          <Label htmlFor="data-fine-proroga">Data fine proroga *</Label>
          <Controller
            name="dataFineProroga"
            control={control}
            render={({ field }) => (
              <Input
                id="data-fine-proroga"
                type="date"
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                value={toDateInputValue(field.value)}
                onChange={(e) =>
                  field.onChange(e.target.value ? new Date(e.target.value) : undefined)
                }
                aria-invalid={!!prorogaErrors.dataFineProroga}
                aria-describedby={
                  prorogaErrors.dataFineProroga ? 'data-fine-proroga-error' : undefined
                }
              />
            )}
          />
          {prorogaErrors.dataFineProroga && (
            <p id="data-fine-proroga-error" className="text-xs text-destructive">
              {prorogaErrors.dataFineProroga.message}
            </p>
          )}
        </div>
      </div>

      {/* Adeguamento ISTAT — checkbox che rivela i nuovi canoni */}
      <div className="flex items-center gap-2">
        <Controller
          name="adeguamentoIstat"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="adeguamento-istat"
              checked={!!field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
              aria-controls="proroga-nuovo-canone"
              aria-expanded={!!adeguamentoIstat}
            />
          )}
        />
        <Label htmlFor="adeguamento-istat" className="cursor-pointer font-normal">
          Applica adeguamento ISTAT / nuovo canone
        </Label>
      </div>

      {adeguamentoIstat && (
        <div id="proroga-nuovo-canone" className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Nuovo canone mensile */}
          <div className="space-y-1">
            <Label htmlFor="nuovo-canone-mensile">Nuovo canone mensile (€) *</Label>
            <Controller
              name="nuovoCanoneMensile"
              control={control}
              render={({ field }) => (
                <Input
                  id="nuovo-canone-mensile"
                  type="number"
                  step="0.01"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      Number.isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber,
                    )
                  }
                  aria-invalid={!!prorogaErrors.nuovoCanoneMensile}
                  aria-describedby={
                    prorogaErrors.nuovoCanoneMensile ? 'nuovo-canone-mensile-error' : undefined
                  }
                />
              )}
            />
            {prorogaErrors.nuovoCanoneMensile && (
              <p id="nuovo-canone-mensile-error" className="text-xs text-destructive">
                {prorogaErrors.nuovoCanoneMensile.message}
              </p>
            )}
          </div>

          {/* Nuovo canone annuale */}
          <div className="space-y-1">
            <Label htmlFor="nuovo-canone-annuale">Nuovo canone annuale (€) *</Label>
            <Controller
              name="nuovoCanoneAnnuale"
              control={control}
              render={({ field }) => (
                <Input
                  id="nuovo-canone-annuale"
                  type="number"
                  step="0.01"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      Number.isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber,
                    )
                  }
                  aria-invalid={!!prorogaErrors.nuovoCanoneAnnuale}
                  aria-describedby={
                    prorogaErrors.nuovoCanoneAnnuale ? 'nuovo-canone-annuale-error' : undefined
                  }
                />
              )}
            />
            {prorogaErrors.nuovoCanoneAnnuale && (
              <p id="nuovo-canone-annuale-error" className="text-xs text-destructive">
                {prorogaErrors.nuovoCanoneAnnuale.message}
              </p>
            )}
          </div>

          {showCoerenzaHint && (
            <p className="text-sm text-amber-600 md:col-span-2" role="status" aria-live="polite">
              Attenzione: il canone annuale dovrebbe essere ~
              {nuovoCanoneMensile != null ? (nuovoCanoneMensile * 12).toFixed(2) : '0'} € (mensile ×
              12, tolleranza ±1%).
            </p>
          )}
        </div>
      )}

      {/*
        Riferimento ai path precompilati: la proroga eredita i dati comuni
        (immobile/catastali/locatore/…) e il `conduttore` dalla pratica madre.
        Questi sono renderizzati altrove (dal host), ma il campo che qui potrebbe
        essere precompilato è la tipologia originale usata come base durata.
        L'indicatore blu/modificato è applicato in modo consistente via
        `prefilledPaths`/`dirtyFields` nei field component che li possiedono.
      */}
      <PrefillIndicatorNote
        prefilledPaths={prefilledPaths}
        dirtyFields={dirtyFields as Record<string, unknown>}
      />
    </div>
  );
}

/**
 * Piccolo helper interno che renderizza — accessibile ma discreto — lo stato
 * di prefill dei campi comuni ereditati (blu = precompilato, muted = modificato).
 * I campi comuni veri e propri sono mostrati dal host; qui garantiamo comunque
 * la presenza dell'hint sr-only pair (mai solo-visivo) per il campo
 * `tipologiaContrattoOriginale` che governa la validazione della durata.
 */
function PrefillIndicatorNote({
  prefilledPaths,
  dirtyFields,
}: {
  prefilledPaths: string[];
  dirtyFields: Record<string, unknown>;
}) {
  const path = 'tipologiaContrattoOriginale';
  const isPrefilled = prefilledPaths.includes(path);
  if (!isPrefilled) return null;
  const isModified = !!dirtyFields[path];
  return (
    <p
      className={cn(
        'inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs',
        isModified
          ? 'border-muted-foreground/30 text-muted-foreground'
          : 'border-blue-400 text-blue-700',
        !isModified && PREFILLED_RING_CLASS,
      )}
    >
      <span aria-hidden="true">{isModified ? 'modificato' : 'precompilato'}</span>
      La durata è validata sulla tipologia originaria del contratto madre.
      <span id={prefilledHintId(path)} className="sr-only">
        {isModified ? MODIFIED_HINT_TEXT : PREFILLED_HINT_TEXT}
      </span>
    </p>
  );
}
